import React, {useMemo, useState} from 'react'
import {Button, Icon, Loading, Pagination, Tabs} from '@therms/atalaya'
import {
  LocationPassDownCard,
  useCurrentUserAnPCheck,
  useLocationContext,
  useLocationPassDowns,
  usePaginationState,
  useTabsState,
} from '@therms/react-modules'
import {PassDownFormModal} from '@src/modules/Locations/components/PassDownFormModal'
import {stringOrUndefined} from '@src/utils/strings'
import {IoMdAdd} from 'react-icons/io'
import {FaArrowDown} from 'react-icons/fa6'

function PassDownList() {
  const {location, locationId} = useLocationContext(true)
  const [showPassDownModal, setShowPassDownModal] = useState<string | boolean>(
    false,
  )
  const [tab, setTab] = useTabsState(`loc-passdowns-tab`, `active`)
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('loc-passdowns', [tab])

  const canCreatePassDowns = useCurrentUserAnPCheck(
    (user) => user.anp.role.ops.permissions.createPassDowns,
  )

  const locationPassDownsArgs = useMemo(() => {
    const now = new Date().toISOString()

    let args: any = {
      locationId,
      pagination: {
        limit: recordsPerPage,
        skip: skippedRecords,
      },
    }

    if (tab === 'active') {
      args = {...args, active: true}
    } else if (tab === 'future') {
      args = {
        ...args,
        active: false,
        activeDate: {gt: now},
      }
    } else if (tab === 'expired') {
      args = {
        ...args,
        active: false,
        expireDate: {lt: now},
      }
    }

    return args
  }, [locationId, recordsPerPage, skippedRecords, tab])

  const {data, loading, refresh} = useLocationPassDowns(locationPassDownsArgs)

  return (
    <div className="gap-y-lg relative flex flex-col">
      <div className="gap-y-base flex w-full flex-row-reverse flex-wrap items-end justify-between">
        {location?.active && canCreatePassDowns && (
          <Button
            className="mt-base w-full sm:mt-0 sm:w-auto"
            onClick={() => setShowPassDownModal(true)}
            variant="main"
          >
            <Icon Icon={IoMdAdd} size="sm" />
            New Pass Down
          </Button>
        )}

        <Tabs initialTabKey={tab} onTabClick={setTab} pills>
          <Tabs.Tab tabKey="active" title="Active" />
          <Tabs.Tab tabKey="future" title="Future" />
          <Tabs.Tab tabKey="expired" title="Expired" />
        </Tabs>
      </div>

      <div className="relative">
        {loading && (
          <Loading
            message="Loading PassDowns"
            messagePosition="vertical"
            overlay
            size="xxl"
          />
        )}

        {data?.passDowns.length ? (
          <div className="gap-y-base flex flex-col">
            {data?.passDowns?.map((passDown) => (
              <LocationPassDownCard
                passDown={passDown}
                key={passDown.id}
                onEdit={
                  location?.active && canCreatePassDowns
                    ? () => setShowPassDownModal(passDown.id)
                    : undefined
                }
                status={tab}
              />
            ))}

            <Pagination
              onPageClick={setSkippedRecords}
              onSetRecordsPerPage={setRecordsPerPage}
              recordsPerPage={recordsPerPage}
              showDescription
              skippedRecords={skippedRecords}
              totalRecords={data?.total}
            />
          </div>
        ) : (
          <div className="flex h-full grow flex-col items-center opacity-50">
            <Icon
              Icon={FaArrowDown}
              color="neutral"
              className="h-[100px] w-[100px]"
            />
            <span className="text-center text-lg font-medium capitalize">
              No {tab} Pass Downs
            </span>
          </div>
        )}
      </div>

      {showPassDownModal && (
        <PassDownFormModal
          passDownId={stringOrUndefined(showPassDownModal)}
          closeHandler={() => setShowPassDownModal(false)}
          onComplete={refresh}
        />
      )}
    </div>
  )
}

export {PassDownList}
